import { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';

import useEvent from 'hooks/useEvent';
import { PRODUCT_CARD_BREAKPOINT_MAX, PRODUCT_CARD_BREAKPOINT_MIN } from 'constants/appConstants';
import { Product } from 'constants/searchTypes';

interface Window {
  visualViewport: VisualViewport;
  screen: Screen;
}

const useRowHasBadge = (list: Product[]) => {
  const [fullList, setFullList] = useState<Product[]>([]);
  const [productsPerRow, setProductsPerRow] = useState<number>(0);

  const getProductsPerRow = useCallback(
    debounce(() => {
      if (typeof window === 'undefined') {
        return;
      }

      const {
        visualViewport: { width: viewportWidth } = {} as VisualViewport,
        screen: { width: screenWidth = 0 }
      }: {
        visualViewport: VisualViewport;
        screen: Screen;
      } = window as Window;

      const width = Math.min(viewportWidth, screenWidth);

      if (width > PRODUCT_CARD_BREAKPOINT_MAX) {
        setProductsPerRow(4);
      } else if (width <= PRODUCT_CARD_BREAKPOINT_MIN) {
        setProductsPerRow(2);
      } else {
        setProductsPerRow(3);
      }
    }, 500),
    []
  );

  useEvent(typeof window !== 'undefined' ? window : null, 'resize', getProductsPerRow);

  useEffect(() => {
    if (!productsPerRow) {
      getProductsPerRow();
    }
  }, [getProductsPerRow, productsPerRow]);

  useEffect(() => {
    const badgeRowsMap: { [key: number]: boolean } = {};
    if (productsPerRow) {
      list.forEach((product, i) => {
        if (product.badges && product.badges.length > 0 && !badgeRowsMap[Math.floor(i / productsPerRow)]) {
          badgeRowsMap[Math.floor(i / productsPerRow)] = true;
        }
      });
    }

    const formattedList = list.map((product, i) => (badgeRowsMap[Math.floor(i / productsPerRow)] ? { ...product, rowHasBadge: true } : product));
    setFullList(formattedList);
  }, [list, productsPerRow]);

  return { formattedList: fullList };
};

export default useRowHasBadge;
